import { useState } from "react";
import { Loader2, ShieldAlert, AlertTriangle, Database } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { useChatbotMetrics } from "../components/chatbot-metrics/useChatbotMetrics";
import { FiltersBar } from "../components/chatbot-metrics/FiltersBar";
import { SyncBar, SyncErrorBanner } from "../components/chatbot-metrics/SyncBar";
import { KpiCards } from "../components/chatbot-metrics/KpiCards";
import { VolumeSection } from "../components/chatbot-metrics/VolumeSection";
import { SpeedSection } from "../components/chatbot-metrics/SpeedSection";
import { QualitySection } from "../components/chatbot-metrics/QualitySection";
import { KnowledgeSection } from "../components/chatbot-metrics/KnowledgeSection";
import { ClientTable } from "../components/chatbot-metrics/ClientTable";
import { DrilldownTable } from "../components/chatbot-metrics/DrilldownTable";
import { SettingsDialog } from "../components/chatbot-metrics/SettingsDialog";

const TONES = { slate: "border-slate-200 text-slate-400", rose: "border-rose-200 text-rose-400" };

const Notice = ({ icon: Icon, title, text, testId, tone = "slate" }) => (
  <div className={`flex flex-col items-center justify-center text-center gap-2 py-16 rounded-xl border bg-white ${TONES[tone]}`} data-testid={testId}>
    <Icon className="w-8 h-8" />
    <p className="font-semibold text-slate-800">{title}</p>
    <p className="text-sm text-slate-500 max-w-md">{text}</p>
  </div>
);

const Dashboard = ({ m }) => {
  const { data } = m;
  return (
    <div className={`space-y-6 transition-opacity ${m.loading ? "opacity-60" : ""}`}>
      <KpiCards kpis={data.kpis} previous={data.previous} settings={data.settings} />
      <VolumeSection data={data} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SpeedSection speed={data.speed} />
        <ClientTable rows={data.clients} />
      </div>
      <QualitySection quality={data.quality} csatStatus={data.csat_status} target={data.settings.csat_target} />
      <KnowledgeSection knowledge={data.knowledge} />
      <DrilldownTable params={m.params} version={m.version} />
    </div>
  );
};

export default function ChatbotMetricsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const m = useChatbotMetrics(isAdmin);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const neverSynced = m.sync && !m.sync.last_success_at;

  let body;
  if (!isAdmin) body = <Notice icon={ShieldAlert} title="Admins only" text="Chatbot Metrics is only available to admins." testId="metrics-admin-only" />;
  else if (m.error && !m.data) body = <Notice icon={AlertTriangle} tone="rose" title="Couldn't load chatbot metrics" text={m.error} testId="metrics-load-error" />;
  else if (!m.data) body = <div className="h-[50vh] flex items-center justify-center" data-testid="metrics-loading"><Loader2 className="w-8 h-8 animate-spin text-[#425B76]" /></div>;
  else if (neverSynced) body = <Notice icon={Database} title="No HubSpot data yet" text={m.sync.running ? "The first sync is running — this takes a couple of minutes." : "Run the first sync to pull the last 90 days of Live Chat conversations."} testId="metrics-never-synced" />;
  else body = <Dashboard m={m} />;

  return (
    <Layout>
      <div className="space-y-5" data-testid="chatbot-metrics-page">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Chatbot Metrics</h1>
            <p className="text-slate-500 text-sm mt-1">How the AI chatbot performs as first responder in the HubSpot Live Chat inbox</p>
          </div>
          {isAdmin && <SyncBar sync={m.sync} onRefresh={m.triggerSync} onSettings={() => setSettingsOpen(true)} />}
        </div>
        {isAdmin && m.sync?.last_error && !m.sync.running && <SyncErrorBanner error={m.sync.last_error} />}
        {isAdmin && <FiltersBar filters={m.filters} setFilters={m.setFilters} options={m.options} />}
        {body}
      </div>
      {isAdmin && <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={m.data?.settings} onSaved={m.reload} />}
    </Layout>
  );
}
